<!-- 
  封装提示弹出框，底部默认两个按钮, 如需一个确定按钮则传入 :showCancel="false"
  左侧显示的是取消按钮文本cancelText，右侧是确定按钮文本 confirmText
  返回数据：点击了x或者遮罩层返回'0'、点击了取消按钮返回'1'、点击了确定按钮返回'2'
-->
<template>
  <van-dialog
    className="custom-dialog"
    v-model:show="visible"
    :title="title"
    :show-cancel-button="false"
    :show-confirm-button="false"
    :close-on-click-overlay="true"
    @confirm="onConfirm"
    @cancel="onCancel"
    z-index="2009"
    @close="onClose"
    :closeable="true"
  >
    <div class="custom-dialog-content">
      <slot name="default">
        <div v-if="message" class="dialog-message">{{ message }}</div>
      </slot>
      <slot name="footer">
        <div class="dialog-footer">
          <button 
            v-if="showCancel" 
            class="dialog-btn dialog-cancel-btn" 
            @click="onCancel"
          >
            {{ cancelText }}
          </button>
          <button 
            class="dialog-btn dialog-confirm-btn" 
            @click="onConfirm"
          >
            {{ confirmText }}
          </button>
        </div>
      </slot>
    </div>
  </van-dialog>
</template>

<script setup lang="ts">
import { ref } from 'vue';

interface Props {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  message: '',
  confirmText: '确定',
  cancelText: '取消',
  showCancel: true,
});

const visible = ref(false);

let resolveFn: ((value: '1' | '2' | '0') => void) | null = null;

// 打开弹框的方法
const open = (): Promise<'1' | '2' | '0'> => {
  visible.value = true;
  return new Promise((resolve) => {
    resolveFn = resolve;
  });
};

// 关闭弹框的方法
const close = () => {
  visible.value = false;
};

const onConfirm = () => {
  if (resolveFn) {
    resolveFn('2');
    resolveFn = null;
  }
  visible.value = false;
};

const onCancel = () => {
  if (resolveFn) {
    resolveFn('1');
    resolveFn = null;
  }
  visible.value = false;
};

const onClose = () => {
  // 点击X关闭按钮
  if (resolveFn) {
    resolveFn('0');
    resolveFn = null;
  }
};

// 暴露方法给父组件
defineExpose({
  open,
  close,
});
</script>

<style scoped>
.custom-dialog-content {
  padding: 3rem 2rem 2rem;
  text-align: center;
}

.dialog-message {
  font-size: 1.7rem;
  color: #000000;
  line-height: 3.2rem;
  word-wrap: break-word;      /* 允许长单词换行 */
  word-break: break-all;      /* 强制换行（适合中文） */
  white-space: normal;        /* 允许换行 */
  max-width: 100%;            /* 限制最大宽度 */
  overflow-wrap: break-word;  /* 现代浏览器推荐使用 */
  text-align: left;
}
.dialog-footer {
  margin-top: 2rem;
  display: flex;
  justify-content: space-around;
  .dialog-btn{
    border-radius: 2rem;
    width: 49%;
    border: 0;
    font-size: 1.6rem;
    line-height: 1;
    padding: 1.2rem 0;
  }
  .dialog-cancel-btn {
    color: #3E8FD2;
    background: #f6faff;
  }
  .dialog-confirm-btn {
    color: #fff;
    background: #3E8FD2;
  }
}
</style>
<style>
/* 全局样式 - 不添加 scoped */
.custom-dialog .van-popup__close-icon {
  top: 1.6rem !important;
  right: 1.6rem !important;
  font-size: 2rem !important;
  color: #000000;
}
</style>
